<template>
  <div>
    <div class="candidate-application-form">
      <div class="candidate-step-menu custom-scrollbar">
        <div class="toggle-sidebar" @click="toggleSidebar">
          <div class="bar1" />
          <div class="bar2" />
          <div class="bar3" />
        </div>

        <div class="nav flex-column nav-pills" id="v-pills-tab" role="tablist" aria-orientation="vertical">
          <div class="step-menu-heading d-flex align-items-center">
            <div>
              <p class="lead mb-0">{{ $t(applyFormData.name) }}</p>
            </div>
          </div>

          <!-- Dynamic apply-form steps -->
          <a
              v-for="(item, index) in validFormSteps"
              :key="`nav-link-${index}`"
              class="nav-link"
              :class="{ complete: formData.length >= index, active: isActiveApplyStep(index) }"
              :id="`v-pills-${index}-tab`"
          >
            <span class="step-number">
              {{ formatStepNumber(index + 1) }}
              <span v-if="formData.length > index" class="complete-icon">
                <app-icon name="check" />
              </span>
              <span class="step-divider"><span class="divider" /></span>
            </span>
            <span class="step-name">{{ item.title }}</span>
          </a>

          <!-- Review step -->
          <a
              class="nav-link"
              :class="{
              complete: showAccountStep || showPaymentStep || paymentCompleted,
              active: readyToSubmit && !showAccountStep && !showPaymentStep
            }"
              id="v-pills-review-tab"
          >
            <span class="step-number">
              {{ formatStepNumber(validFormSteps.length + 1) }}
              <span v-if="showAccountStep || showPaymentStep || paymentCompleted" class="complete-icon">
                <app-icon name="check" />
              </span>
              <span class="step-divider"><span class="divider" /></span>
            </span>
            <span class="step-name">Review</span>
          </a>

          <!-- Account step (only shown if required) -->
          <a
              v-if="requiresAccountStep"
              class="nav-link"
              :class="{ complete: showPaymentStep || paymentCompleted, active: showAccountStep }"
              id="v-pills-account-tab"
          >
            <span class="step-number">
              {{ formatStepNumber(validFormSteps.length + 2) }}
              <span v-if="showPaymentStep || paymentCompleted" class="complete-icon">
                <app-icon name="check" />
              </span>
              <span class="step-divider"><span class="divider" /></span>
            </span>
            <span class="step-name">Account</span>
          </a>

          <!-- Payment step (only shown if required) -->
          <a
              v-if="requiresPaymentStep"
              class="nav-link"
              :class="{ complete: paymentCompleted, active: showPaymentStep }"
              id="v-pills-payment-tab"
          >
            <span class="step-number">
              {{ formatStepNumber(validFormSteps.length + (requiresAccountStep ? 3 : 2)) }}
              <span v-if="paymentCompleted" class="complete-icon">
                <app-icon name="check" />
              </span>
              <span class="step-divider"><span class="divider" /></span>
            </span>
            <span class="step-name">Payment</span>
          </a>
        </div>
      </div>

      <div class="candidate-step-content">
        <div class="tab-content" id="v-pills-tabContent">
          <div class="tab-pane fade show active" v-if="isEmailVerified">
            <h4 class="mb-5">{{ currentStepTitle }}</h4>

            <div class="row">
              <div class="col-lg-9 col-xl-7">
                <!-- FORM STEPS -->
                <div v-if="!readyToSubmit && !showAccountStep && !showPaymentStep">
                  <form-step
                      v-for="(form, index) in validFormSteps"
                      v-if="formData.length === index && form.is_visible"
                      :key="form.id"
                      :selected-form="selectedForm"
                      :steps="validFormSteps.length"
                      :index="index"
                      :form-body="form"
                      @next="goNextStep"
                      @previous="goPrevStep"
                  />
                </div>

                <!-- REVIEW STEP -->
                <div v-if="readyToSubmit && !showAccountStep && !showPaymentStep">
                  <div v-for="(step, stepIndex) in formData" :key="`step-${stepIndex}`">
                    <h4>{{ step.title }}</h4>

                    <div
                        v-for="(form, formIndex) in step.items"
                        :key="`form-${formIndex}`"
                        class="my-2 text-muted"
                    >
                      <table class="table table-borderless">
                        <tbody>
                        <tr v-for="(field, index) in form.fields" :key="`item-${index}`">
                          <td
                              class="w-50 px-0"
                              :colspan="field.type === 'custom-form' ? 2 : 1"
                              :class="{ 'border-bottom': field.type === 'custom-form' }"
                          >
                            <template v-if="field.type === 'custom-form'">
                              <label
                                  class="mb-3 text-black-50 font-weight-bold text-uppercase font-2xl"
                                  style="letter-spacing: 6px;"
                              >
                                {{ field.title }}
                              </label>
                            </template>

                            <template v-else>
                              <span class="text-muted">{{ field.title }}</span>
                            </template>

                            <table v-if="field.type === 'custom-form'" class="table table-borderless m-0">
                              <tbody>
                              <tr v-for="(f, i) in field.fields" :key="`custom-field-${index}-${i}`">
                                <td class="w-50 px-0">
                                  <span class="text-muted">{{ f.title }}</span>
                                </td>
                                <td class="w-50 px-0">
                                      <span
                                          class="text-muted"
                                          v-if="['text','email','number','textarea','tel-input'].includes(f.type)"
                                      >
                                        {{ f.value || '-' }}
                                      </span>

                                  <span class="text-muted" v-else-if="['radio','checkbox','select'].includes(f.type)">
                                        <app-input
                                            :id="`item-${f.id}-${index}`"
                                            :key="`item-${f.id}-${index}`"
                                            :radioCheckboxName="f.title"
                                            v-model="f.value"
                                            :list="(f.options || []).map(i => ({ id: i, value: i }))"
                                            :type="f.type"
                                            :disabled="true"
                                        />
                                      </span>

                                  <span class="text-muted" v-else>
                                        {{ f.type === 'date' ? formatDateToLocal(f.value) || '-' : f.value || '-' }}
                                      </span>
                                </td>
                              </tr>
                              </tbody>
                            </table>
                          </td>

                          <td class="w-50 px-0" v-if="field.type !== 'custom-form'">
                              <span v-if="field.type === 'dropzone'" class="text-muted">
                                <div v-for="file in (field.value || [])" :key="file.name">
                                  <span>{{ file.name }}</span>
                                </div>
                              </span>

                            <span
                                class="text-muted"
                                v-else-if="['text','email','number','textarea','tel-input'].includes(field.type)"
                            >
                                {{ field.value || '-' }}
                              </span>

                            <span class="text-muted" v-else-if="['radio','checkbox','select'].includes(field.type)">
                                <app-input
                                    :id="`item-${field.id}-${index}`"
                                    :key="`item-${field.id}-${index}`"
                                    :radioCheckboxName="field.title"
                                    v-model="field.value"
                                    :list="(field.options || []).map(i => ({ id: i, value: i }))"
                                    :type="field.type"
                                    :disabled="true"
                                />
                              </span>

                            <span class="text-muted" v-else>
                                {{ field.type === 'date' ? formatDateToLocal(field.value) || '-' : field.value || '-' }}
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div class="d-flex align-items-center">
                    <button type="button" class="btn btn-outline-secondary mr-3" @click.prevent="editApplication">
                      <app-icon name="chevron-left" class="mr-2" />
                      {{ $t('previous') }}
                    </button>

                    <app-submit-button :label="reviewCtaLabel" :loading="preloader" @click="handleReviewContinue" />
                  </div>
                </div>

                <!-- ACCOUNT STEP -->
                <div v-if="showAccountStep && !showPaymentStep" class="account-step">
                  <h4 class="mb-4">Create your account</h4>

                  <div v-if="accountError" class="alert alert-danger mb-3">
                    {{ accountError }}
                  </div>

                  <div class="form-group">
                    <label>Email (Login)</label>
                    <input type="email" class="form-control" :value="accountEmail" disabled />
                    <small class="text-muted d-block mt-1">
                      This is the verified email address.
                    </small>
                  </div>

                  <div class="form-group">
                    <label>Password</label>
                    <div class="input-group">
                      <input
                          class="form-control"
                          :type="showPassword ? 'text' : 'password'"
                          v-model="accountPassword"
                          placeholder="Enter password"
                          autocomplete="new-password"
                      />
                      <div class="input-group-append">
                        <button class="btn btn-outline-secondary" type="button" @click="showPassword = !showPassword">
                          <app-icon :name="showPassword ? 'eye-off' : 'eye'" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>Confirm Password</label>
                    <div class="input-group">
                      <input
                          class="form-control"
                          :type="showPasswordConfirm ? 'text' : 'password'"
                          v-model="accountPasswordConfirm"
                          placeholder="Confirm password"
                          autocomplete="new-password"
                      />
                      <div class="input-group-append">
                        <button class="btn btn-outline-secondary" type="button" @click="showPasswordConfirm = !showPasswordConfirm">
                          <app-icon :name="showPasswordConfirm ? 'eye-off' : 'eye'" />
                        </button>
                      </div>
                    </div>

                    <small v-if="passwordMismatch" class="text-danger d-block mt-1">
                      Passwords do not match
                    </small>
                  </div>

                  <div class="d-flex align-items-center">
                    <button type="button" class="btn btn-outline-secondary mr-3" @click.prevent="goBackToReview">
                      <app-icon name="chevron-left" class="mr-2" />
                      {{ $t('previous') }}
                    </button>

                    <app-submit-button :label="accountCtaLabel" :loading="preloader" @click="createApplicationAndGoNext" />
                  </div>
                </div>

                <!-- PAYMENT STEP -->
                <div v-if="showPaymentStep" class="payment-step">
                  <h4>Payment</h4>
                  <p class="mb-4">Total amount to pay: €{{ paymentAmount }}</p>

                  <div v-if="paymentError" class="alert alert-danger mb-4">
                    {{ paymentError }}
                  </div>

                  <paypal-payment
                      v-if="applicantId && !paymentCompleted"
                      :amount="paymentAmount"
                      currency="EUR"
                      :job-slug="applyFormData.slug"
                      :applicant-id="applicantId"
                      @payment-completed="handlePaymentCompleted"
                      @payment-failed="handlePaymentFailed"
                      @payment-error="handlePaymentError"
                  />

                  <div v-else-if="!applicantId" class="alert alert-warning">
                    Preparing payment information...
                  </div>

                  <div v-if="paymentCompleted" class="alert alert-success">
                    Payment completed successfully! Your application has been submitted.
                  </div>

                  <div v-if="showPaymentStep && !paymentCompleted" class="mt-3">
                    <button type="button" class="btn btn-outline-secondary" @click.prevent="goPrevStep">
                      <app-icon name="chevron-left" class="mr-2" />
                      {{ $t('previous') }}
                    </button>
                  </div>
                </div>
              </div><!-- col -->
            </div><!-- row -->
          </div>
        </div>
      </div>
    </div>

    <!-- EMAIL MODAL ONLY FOR GUEST -->
    <candidate-email-verification-modal
        v-if="emailVerificationModal"
        :modal-id="emailVerificationModalId"
        :job-post-id="applyFormData.id"
        @cancelApplication="goToJobPostView"
        @verifiedData="afterVerifiedEmail"
        @close="closeEmailVerificationModal"
    />

    <app-confirmation-modal
        v-if="showConfirmationModal"
        :icon="confirmationModalIcon"
        :hide-second-button="true"
        :title="confirmationModalTitle"
        :message="confirmationModalMessage"
        :modal-class="confirmationModalClass"
        modal-id="confirmation-modal"
        :first-button-name="$t('ok')"
        :second-button-name="''"
        @confirmed="handleConfirmation"
    />
  </div>
</template>

<script>
import { TENANT_BASE_URL } from "../../../../Config/UrlHelper";
import FormStep from "./FormStep.vue";
import PaypalPayment from "../../../Paypal/PaypalPayment.vue";
import { urlGenerator } from "../../../../Helpers/AxiosHelper";
import { formatDateToLocal } from "../../../../Helpers/DateTimeHelper";
import { FormMixin } from "../../../../../core/mixins/form/FormMixin";

export default {
  props: ["applyFormData"],
  mixins: [FormMixin],
  components: { FormStep, PaypalPayment },
  data() {
    return {
      formatDateToLocal,
      preloader: false,

      emailVerificationModal: false,
      emailVerificationModalId: "email-verification-modal",
      authChecked: false,

      showConfirmationModal: false,
      confirmationModalMessage: "",
      confirmationModalTitle: "",
      confirmationModalClass: "",
      confirmationModalIcon: "",

      applicantId: null,
      appliedDone: false,

      // payment
      paymentAmount: 50,
      paymentCompleted: false,
      showPaymentStep: false,
      paymentError: null,

      // account
      showAccountStep: false,
      accountEmail: "",
      accountPassword: "",
      accountPasswordConfirm: "",
      showPassword: false,
      showPasswordConfirm: false,
      accountError: null,

      // auth/existing detection
      isAuthenticated: false,
      existingApplicantData: null,

      formDataBody:
          (typeof this.applyFormData.apply_form_settings === "string"
                  ? JSON.parse(this.applyFormData.apply_form_settings)
                  : this.applyFormData.apply_form_settings
          ).filter((item) => item.is_visible),

      formData: [],
      selectedForm: null,
      readyToSubmit: false,
      applicationSubmitted: false,
      isEmailVerified: false,
      redirectAfterConfirmation: false,
    };
  },
  computed: {
    validFormSteps() {
      return this.formDataBody.filter((item) => item.is_visible);
    },

    requiresAccountStep() {
      if (this.isAuthenticated) return false;
      if (this.existingApplicantData) return false;
      return true;
    },

    requiresPaymentStep() {
      if (this.isAuthenticated) return false;
      return Number(this.paymentAmount) > 0;
    },

    currentStepTitle() {
      if (this.showPaymentStep) return "Payment";
      if (this.showAccountStep) return "Account";
      if (this.readyToSubmit) return "Review Your Application";
      return this.validFormSteps[this.formData.length]?.title || "";
    },

    passwordMismatch() {
      if (!this.accountPassword && !this.accountPasswordConfirm) return false;
      return this.accountPassword !== this.accountPasswordConfirm;
    },

    reviewCtaLabel() {
      if (this.isAuthenticated) return this.$t("finish");
      if (this.requiresAccountStep) return "Continue to Account";
      if (this.requiresPaymentStep) return this.$t("continue_to_payment");
      return this.$t("finish");
    },

    accountCtaLabel() {
      if (this.requiresPaymentStep) return this.$t("continue_to_payment");
      return this.$t("finish");
    }
  },
  methods: {
    async detectAuthUser() {
      try {
        const res = await axios.get(`${TENANT_BASE_URL}/login-user`, { withCredentials: true });
        const u = res?.data || null;

        if (u && u.id) {
          this.isAuthenticated = true;
          this.isEmailVerified = true;
          this.emailVerificationModal = false;

          if (u.email) this.accountEmail = u.email;

          this.showAccountStep = false;
          this.showPaymentStep = false;

          return true;
        }
      } catch (e) {
        // ignore
      }
      return false;
    },

    isActiveApplyStep(index) {
      return !this.readyToSubmit && !this.showAccountStep && !this.showPaymentStep && this.formData.length === index;
    },

    formatStepNumber(number) {
      return number > 10 ? number : `0${number}`;
    },

    toggleSidebar() {
      $(".toggle-sidebar").toggleClass("change");
      $(".candidate-step-menu").toggleClass("active");
    },

    formatDateForBackend(date) {
      if (!date) return null;
      if (date instanceof Date) return date.toISOString().split("T")[0];
      if (typeof date === "string") {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? null : parsed.toISOString().split("T")[0];
      }
      return null;
    },

    // ✅ IMPORTANT FIX:
    // Your backend expects:
    // - "resume" (single file) for the main CV upload
    // - "answers" (JSON string) to store all step data into DB (job_applicants.answers)
    // - "apply_form_setting" (JSON string) to store form structure used
    buildAnswersPayload() {
      // This is what you see in your network inspector as "answers": [...]
      // We store exactly what the user filled, step by step.
      return JSON.stringify(this.formData || []);
    },

    buildApplyFormSettingPayload() {
      // Use the original form config coming from the job post (applyFormData)
      const raw = this.applyFormData.apply_form_settings ?? this.applyFormData.apply_form_setting ?? [];
      return typeof raw === "string" ? raw : JSON.stringify(raw);
    },

    extractResumeFileFromFormData() {
      // ✅ FIX: handle multiple possible shapes from dropzone
      for (const step of this.formData || []) {
        for (const item of step.items || []) {
          for (const field of item.fields || []) {
            if (field.type === "dropzone" && Array.isArray(field.value) && field.value.length) {
              const first = field.value[0];

              // Case 1: already File/Blob
              if (first instanceof File || first instanceof Blob) return first;

              // Case 2: wrapped objects
              if (first?.file instanceof File || first?.file instanceof Blob) return first.file;
              if (first?.blob instanceof File || first?.blob instanceof Blob) return first.blob;

              // Otherwise it's probably only metadata (name/url)
              return null;
            }
          }
        }
      }
      return null;
    },

    handleReviewContinue() {
      if (this.isAuthenticated) {
        this.createApplicationAndGoNext();
        return;
      }

      if (this.requiresAccountStep) {
        this.accountError = null;
        this.showAccountStep = true;
        return;
      }

      this.createApplicationAndGoNext();
    },

    goBackToReview() {
      this.accountError = null;
      this.showAccountStep = false;
      this.readyToSubmit = true;
    },

    async createApplicationAndGoNext() {
      if (this.requiresAccountStep) {
        this.accountError = null;

        if (!this.accountEmail) {
          this.accountError = "Email is required";
          return;
        }
        if (!this.accountPassword || this.accountPassword.length < 8) {
          this.accountError = "Password must be at least 8 characters";
          return;
        }
        if (this.passwordMismatch) {
          this.accountError = "Passwords do not match";
          return;
        }
      }

      this.preloader = true;
      this.paymentError = null;

      try {
        const basicInfo = this.formData.find((i) => i.key === "basic_information");
        if (!basicInfo) throw new Error("Basic information form not found");

        const payload = new FormData();

        const applicantData = {
          first_name: basicInfo.items[0].fields[0].value?.trim() || "",
          last_name: basicInfo.items[0].fields[1].value?.trim() || "",
          email: basicInfo.items[0].fields[2].value?.trim() || (this.accountEmail || ""),
          gender: (basicInfo.items[0].fields[3].value?.toString()?.toLowerCase() || ""),
          phone: basicInfo.items[0].fields[5].value?.trim() || "",
          date_of_birth: this.formatDateForBackend(basicInfo.items[0].fields[4].value),
          job_post_id: this.applyFormData.id,
        };

        if (this.requiresAccountStep) {
          applicantData.password = this.accountPassword;
          applicantData.password_confirmation = this.accountPasswordConfirm;
        }

        if (!applicantData.first_name) throw new Error("First name is required");
        if (!applicantData.last_name) throw new Error("Last name is required");
        if (!applicantData.email) throw new Error("Email is required");
        if (!applicantData.gender) throw new Error("Gender is required");
        if (!["male", "female", "other"].includes(applicantData.gender)) {
          throw new Error("Please select a valid gender (Male, Female, or Other)");
        }
        if (!applicantData.phone) throw new Error("Phone number is required");
        if (!applicantData.date_of_birth) throw new Error("Date of birth is required");

        // Base fields
        Object.entries(applicantData).forEach(([k, v]) => {
          if (v !== undefined && v !== null) payload.append(k, v);
        });

        payload.append("answers", this.buildAnswersPayload());
        payload.append("apply_form_setting", this.buildApplyFormSettingPayload());

        // ✅ FIX: append resume with filename
        const resumeFile = this.extractResumeFileFromFormData();
        if (resumeFile) {
          payload.append("resume", resumeFile, resumeFile.name || "resume.pdf");
        }

        const response = await axios.post(`${TENANT_BASE_URL}/applicants`, payload, {
          withCredentials: true,
        });

        const createdId =
            response.data?.id ||
            response.data?.data?.id ||
            response.data?.applicant_id ||
            response.data?.applicant?.id ||
            null;

        if (!createdId) {
          throw new Error("Application created but no id returned from server. Backend must return {id}.");
        }

        this.applicantId = createdId;

        if (this.isAuthenticated) {
          this.showSuccess("Application submitted successfully!");
          return;
        }

        if (this.requiresPaymentStep) {
          this.showPaymentStep = true;
          this.showAccountStep = false;
          this.readyToSubmit = false;
        } else {
          this.showSuccess("Application submitted successfully!");
        }
      } catch (error) {
        console.error("Application/payment preparation error:", error);

        if (error.response?.status === 422) {
          const errors = error.response.data.errors || {};
          this.paymentError = Object.values(errors).flat().join(", ");
        } else if (error.response?.data?.message === "candidate_already_exist") {
          this.paymentError = this.$t("candidate_already_exist");
          this.showConfirmationModal = true;
          this.confirmationModalMessage = this.paymentError;
          this.confirmationModalClass = "danger";
          this.confirmationModalTitle = this.$t("error");
          this.confirmationModalIcon = "x-circle";
        } else {
          this.paymentError = error.message || "Failed to prepare. Please try again.";
        }
      } finally {
        this.preloader = false;
      }
    },

    handlePaymentCompleted(response) {
      this.paymentCompleted = true;
      this.applicationSubmitted = true;
      this.showSuccess(response.message || "Payment and application submitted successfully!");
    },

    handlePaymentFailed(error) {
      this.paymentError = error.response?.data?.message || "Payment failed. Please try again.";
      this.showConfirmationModal = true;
      this.confirmationModalMessage = this.paymentError;
      this.confirmationModalClass = "danger";
      this.confirmationModalTitle = this.$t("payment_failed");
      this.confirmationModalIcon = "x-circle";
    },

    handlePaymentError(error) {
      this.paymentError = error.message || "An error occurred during payment.";
      this.showConfirmationModal = true;
      this.confirmationModalMessage = this.paymentError;
      this.confirmationModalClass = "danger";
      this.confirmationModalTitle = this.$t("error");
      this.confirmationModalIcon = "x-circle";
    },

    showSuccess(message) {
      this.confirmationModalMessage = message;
      this.confirmationModalClass = "success";
      this.confirmationModalTitle = this.$t("thank_you");
      this.confirmationModalIcon = "check-circle";
      this.appliedDone = true;
      this.applicationSubmitted = true;
      this.redirectAfterConfirmation = true;
      this.showConfirmationModal = true;
    },

    editApplication() {
      this.readyToSubmit = false;
      this.goPrevStep();
    },

    goToJobPostView() {
      window.location = urlGenerator(`${TENANT_BASE_URL}/${this.applyFormData.slug}`);
    },

    afterVerifiedEmail(email, data) {
      this.existingApplicantData = data || null;

      const basic_information_form = this.formDataBody.find((i) => i.key === "basic_information");
      if (basic_information_form?.items?.[0]?.fields?.[2]) {
        basic_information_form.items[0].fields[2].value = email;
        basic_information_form.items[0].fields[2].disabled = true;
      }

      this.accountEmail = email;

      if (data) {
        basic_information_form.items[0].fields[0].value = data.first_name;
        basic_information_form.items[0].fields[1].value = data.last_name;
        basic_information_form.items[0].fields[3].value = data.gender;
        basic_information_form.items[0].fields[4].value = data.date_of_birth;
        basic_information_form.items[0].fields[5].value = data.phone;

        if (data.id) this.applicantId = data.id;
      } else {
        this.emailVerificationModal = false;
      }

      this.isEmailVerified = true;
    },

    closeEmailVerificationModal() {
      $(`#${this.emailVerificationModalId}`).modal("hide");
      this.emailVerificationModal = false;
    },

    // ✅ FIX: ALWAYS store the step (including last step)
    goNextStep(data, isFinalStep) {
      this.formData.push(data);
      this.selectedForm = null;

      if (isFinalStep) {
        this.readyToSubmit = true;
      }
    },

    goPrevStep() {
      if (this.showPaymentStep) {
        this.showPaymentStep = false;
        if (this.requiresAccountStep) {
          this.showAccountStep = true;
          this.readyToSubmit = false;
        } else {
          this.showAccountStep = false;
          this.readyToSubmit = true;
        }
        return;
      }

      if (this.showAccountStep) {
        this.showAccountStep = false;
        this.readyToSubmit = true;
        return;
      }

      const fd = this.formData.pop();
      if (fd) {
        fd.items.map((i) => ({
          ...i,
          fields: i.fields.map((f) => ({ ...f, value: f.type === "dropzone" ? "" : f.value })),
        }));
        this.selectedForm = fd;
      }
      this.readyToSubmit = false;
    },

    handleConfirmation() {
      if (this.redirectAfterConfirmation) {
        window.location.href = "/";
      } else {
        this.showConfirmationModal = false;
      }
    },
  },

  async mounted() {
    this.authChecked = false;
    await this.detectAuthUser();
    this.authChecked = true;

    if (!this.isAuthenticated) {
      this.emailVerificationModal = true;
    }
  },
};
</script>

<style scoped>
.payment-step,
.account-step {
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-top: 20px;
}

.candidate-application-form {
  display: flex;
  min-height: 100vh;
}

.candidate-step-menu {
  width: 300px;
  background: #fff;
  border-right: 1px solid #eee;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
}

.candidate-step-content {
  flex: 1;
  padding: 30px;
}

.toggle-sidebar {
  display: none;
  cursor: pointer;
  margin-bottom: 20px;
}

.toggle-sidebar div {
  width: 25px;
  height: 3px;
  background-color: #333;
  margin: 5px 0;
  transition: 0.4s;
}

.nav-link {
  position: relative;
  padding: 15px 0;
  color: #6c757d;
  border-radius: 0;
}

.nav-link.active {
  color: #007bff;
  background-color: transparent;
}

.nav-link.complete {
  color: #28a745;
}

.step-number {
  display: inline-block;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 50%;
  background-color: #f8f9fa;
  margin-right: 10px;
  position: relative;
}

.nav-link.active .step-number {
  background-color: #007bff;
  color: white;
}

.nav-link.complete .step-number {
  background-color: #28a745;
  color: white;
}

.complete-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
}

.step-divider {
  position: absolute;
  left: 15px;
  bottom: -15px;
  width: 1px;
  height: 15px;
  background-color: #dee2e6;
}

.step-name {
  vertical-align: middle;
}

@media (max-width: 992px) {
  .candidate-step-menu {
    position: fixed;
    left: -300px;
    top: 0;
    z-index: 1000;
    transition: 0.3s;
  }

  .candidate-step-menu.active {
    left: 0;
  }

  .toggle-sidebar {
    display: inline-block;
  }

  .toggle-sidebar.change .bar1 {
    transform: rotate(-45deg) translate(-5px, 6px);
  }

  .toggle-sidebar.change .bar2 {
    opacity: 0;
  }

  .toggle-sidebar.change .bar3 {
    transform: rotate(45deg) translate(-5px, -6px);
  }

  .candidate-step-content {
    margin-left: 0;
  }
}
</style>
